import { Component, Inject, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators,
} from '@angular/forms';
import {
	MAT_BOTTOM_SHEET_DATA,
	MatBottomSheetModule,
	MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { NgForOf, NgIf } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

interface NoteSubject {
	subject: string;
	description: string;
}

@Component({
	selector: 'app-stage-change-note-form',
	templateUrl: 'stage-change-note-form.component.html',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		NgForOf,
		NgIf,
		ReactiveFormsModule,
		MatInputModule,
		FormsModule,
		MatFormFieldModule,
		MatBottomSheetModule,
		MatButtonModule,
		MatIconModule,
		MatSelectModule,
		MatOptionModule,
	],
})
export class StageChangeNoteFormComponent implements OnInit, OnDestroy {
	bottomSheetRef =
		inject<MatBottomSheetRef<StageChangeNoteFormComponent>>(MatBottomSheetRef);

	newStage: string;
	applicationId: string;
	stageChangeNoteFormGroup: FormGroup;
	noteSubjects: NoteSubject[] = [];
	isLoadingSubjects: boolean = false;

	protected _onDestroy = new Subject<void>();

	constructor(
		@Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
		private readonly apiService: LegacyApiService,
		private readonly formBuilder: FormBuilder
	) {
		if (this.data) {
			this.newStage = this.data.newStage;
			this.applicationId = this.data.applicationId;
		}

		this.stageChangeNoteFormGroup = this.formBuilder.group({
			subject: ['', Validators.required],
			description: ['', [Validators.required, Validators.maxLength(250)]],
			communication: ['INT', [Validators.required]],
			note: [''],
		});
	}

	ngOnInit() {
		this.loadNoteSubjects();

		// Watch for subject changes to auto-update description
		this.stageChangeNoteFormGroup
			.get('subject')
			?.valueChanges.pipe(takeUntil(this._onDestroy))
			.subscribe((selectedSubject) => {
				const subject = this.noteSubjects.find(
					(s) => s.subject === selectedSubject
				);
				if (subject) {
					this.stageChangeNoteFormGroup.patchValue({
						description: subject.description,
					});
				}
			});
	}

	loadNoteSubjects(): void {
		this.isLoadingSubjects = true;
		this.stageChangeNoteFormGroup.get('subject')?.disable();

		this.apiService.getNoteSubjects().subscribe({
			next: (res) => {
				if (res?.data) {
					this.noteSubjects = res.data;
					this.stageChangeNoteFormGroup.get('subject')?.enable();
				}
				this.isLoadingSubjects = false;
			},
			error: (error) => {
				console.error('Failed to load note subjects:', error);
				this.isLoadingSubjects = false;
				this.stageChangeNoteFormGroup.get('subject')?.enable();
			},
		});
	}

	onSubmit(): void {
		if (this.stageChangeNoteFormGroup.valid) {
			const formValue = this.stageChangeNoteFormGroup.getRawValue();
			this.bottomSheetRef.dismiss({
				note: formValue,
			});
		}
	}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}
}
